import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatCurrency, formatDate, formatApiErrorDetail } from "../lib/api";
import StatusBadge from "../components/app/StatusBadge";
import { ArrowLeft, PencilSimple, PaperPlaneTilt, Copy, Printer, CheckCircle, XCircle, FileText, Link as LinkIcon, EnvelopeSimple, Bell } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailModal, setEmailModal] = useState(null); // null | "send" | "reminder"
  const [emailTo, setEmailTo] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [sending, setSending] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/quotes/${id}`);
      setQ(data);
    } catch (e) {
      toast.error("Preventivo non trovato");
      navigate("/preventivi");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [id]);

  async function sendQuote() {
    try {
      await api.post(`/quotes/${id}/send`);
      toast.success("Preventivo pronto da inviare");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  }

  function openEmailModal(kind) {
    setEmailTo(q?.cliente_snapshot?.email || "");
    setEmailMsg("");
    setEmailModal(kind);
  }

  async function sendEmail(e) {
    e.preventDefault();
    setSending(true);
    try {
      const endpoint = emailModal === "reminder" ? `/quotes/${id}/reminder` : `/quotes/${id}/email`;
      await api.post(endpoint, { to_email: emailTo, custom_message: emailMsg || null });
      toast.success(emailModal === "reminder" ? "Promemoria inviato" : "Email inviata al cliente");
      setEmailModal(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally {
      setSending(false);
    }
  }

  async function changeStatus(stato) {
    try {
      await api.post(`/quotes/${id}/status`, { stato });
      toast.success("Stato aggiornato");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  }

  function publicUrl() {
    if (!q?.public_token) return null;
    return `${window.location.origin}/p/${q.public_token}`;
  }

  function copyLink() {
    const url = publicUrl();
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast.success("Link copiato");
  }

  if (loading || !q) return <div className="p-12 text-sm text-[#64748B]">Caricamento…</div>;

  const canEdit = q.stato !== "firmato";

  return (
    <div className="p-4 md:p-12 max-w-[1100px] mx-auto">
      <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-[0.15em] text-[#64748B] hover:text-[#0A0A0A] flex items-center gap-2 mb-6 no-print">
        <ArrowLeft size={14} /> Indietro
      </button>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 no-print">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3 flex items-center gap-3">
            Preventivo <StatusBadge stato={q.stato} />
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">{q.numero}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && (
            <button onClick={() => navigate(`/preventivi/${id}/modifica`)} data-testid="edit-quote-btn" className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7] flex items-center gap-2">
              <PencilSimple size={14} weight="bold" /> Modifica
            </button>
          )}
          <button onClick={() => window.print()} data-testid="print-quote-btn" className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7] flex items-center gap-2">
            <Printer size={14} weight="bold" /> Stampa / PDF
          </button>
          {q.stato === "bozza" && (
            <button onClick={sendQuote} data-testid="send-quote-btn" className="btn-primary px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2">
              <PaperPlaneTilt size={14} weight="bold" /> Genera Link Firma
            </button>
          )}
          {q.public_token && q.stato !== "firmato" && (
            <>
              <button onClick={() => openEmailModal("send")} data-testid="email-quote-btn" className="btn-primary px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2">
                <EnvelopeSimple size={14} weight="bold" /> Invia Email
              </button>
              {q.email_sent_at && (
                <button onClick={() => openEmailModal("reminder")} data-testid="reminder-btn" className="px-4 py-2.5 text-xs uppercase tracking-[0.15em] font-bold border border-[#E65C00] text-[#E65C00] rounded-sm hover:bg-[#E65C00] hover:text-white flex items-center gap-2 transition-all">
                  <Bell size={14} weight="bold" /> Promemoria
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Link pubblico — visibile fino a saldo completo (sopravvive alla firma per consentire il pagamento) */}
      {q.public_token && q.payment_status !== "pagato" && (
        <div className="swiss-card p-4 mb-6 bg-[#0033A0]/5 border-[#0033A0]/20 no-print">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <LinkIcon size={20} weight="bold" className="text-[#0033A0] flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#0033A0] font-bold">
                  {q.stato === "firmato"
                    ? (q.payment_status === "abbonamento_attivo" || q.payment_status === "acconto_versato"
                        ? "Link Pagamento (saldo residuo)"
                        : "Link Pagamento")
                    : "Link Pubblico — Firma & Pagamento"}
                </div>
                <div className="text-xs font-mono text-[#0A0A0A] mt-1 break-all">{publicUrl()}</div>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={copyLink} data-testid="copy-link-btn" className="px-3 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#0033A0] text-[#0033A0] rounded-sm hover:bg-[#0033A0] hover:text-white flex items-center gap-1.5 transition-all">
                <Copy size={14} weight="bold" /> Copia
              </button>
              <a href={publicUrl()} target="_blank" rel="noreferrer" className="px-3 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#0A0A0A] rounded-sm hover:bg-[#0A0A0A] hover:text-white flex items-center gap-1.5 transition-all">
                Apri
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Quote document */}
      <div className="swiss-card print-page p-4 sm:p-8 md:p-12">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 pb-8 border-b-2 border-[#0A0A0A]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">Preventivo</div>
            <div className="font-display text-3xl font-black tracking-tighter">{q.numero}</div>
            <div className="text-xs text-[#64748B] mt-2 font-mono">
              Emesso il {formatDate(q.data_emissione)}
              {q.data_validita && <> · Valido fino al {formatDate(q.data_validita)}</>}
            </div>
          </div>
          <div className="text-right flex items-start gap-3 justify-end">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">Emittente</div>
              <div className="font-display text-xl font-bold">{q.azienda?.ragione_sociale || "Sa.Gi Group"}</div>
              <div className="text-xs text-[#64748B] mt-1 space-y-0.5">
                {q.azienda?.indirizzo && <div>{q.azienda.indirizzo}</div>}
                {(q.azienda?.cap || q.azienda?.citta) && (
                  <div>{q.azienda.cap} {q.azienda.citta} {q.azienda.provincia ? `(${q.azienda.provincia})` : ""}</div>
                )}
                {q.azienda?.p_iva && <div className="font-mono">P.IVA {q.azienda.p_iva}</div>}
                {q.azienda?.telefono && <div>Tel. {q.azienda.telefono}</div>}
                {q.azienda?.email && <div>{q.azienda.email}</div>}
                {q.azienda?.sito_web && <div>{q.azienda.sito_web}</div>}
              </div>
            </div>
            {q.azienda?.logo_url && (
              <img src={q.azienda.logo_url} alt="Logo" className="w-20 h-20 object-contain" />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-8 border-b border-[#E2E8F0]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">Cliente</div>
            <div className="font-display text-lg font-bold">{q.cliente_snapshot?.ragione_sociale}</div>
            <div className="text-sm text-[#64748B] mt-2 space-y-0.5">
              {q.cliente_snapshot?.indirizzo && <div>{q.cliente_snapshot.indirizzo}</div>}
              {(q.cliente_snapshot?.cap || q.cliente_snapshot?.citta) && (
                <div>{q.cliente_snapshot.cap} {q.cliente_snapshot.citta} {q.cliente_snapshot.provincia ? `(${q.cliente_snapshot.provincia})` : ""}</div>
              )}
              {q.cliente_snapshot?.p_iva && <div className="font-mono">P.IVA {q.cliente_snapshot.p_iva}</div>}
              {q.cliente_snapshot?.email && <div>{q.cliente_snapshot.email}</div>}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">Referente</div>
            <div className="text-sm">{q.cliente_snapshot?.referente || "—"}</div>
            <div className="text-sm text-[#64748B]">{q.cliente_snapshot?.telefono}</div>
          </div>
        </div>

        {/* Items */}
        <div className="py-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-4">Voci</div>
          <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
            <table className="w-full text-sm min-w-[500px]">
            <thead>
              <tr className="border-b border-[#0A0A0A] text-left">
                <th className="py-2 text-[10px] uppercase tracking-[0.15em] font-bold">Servizio</th>
                <th className="py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-right">Q.tà</th>
                <th className="py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-right">Prezzo</th>
                <th className="py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-right">Sconto</th>
                <th className="py-2 text-[10px] uppercase tracking-[0.15em] font-bold text-right">Importo</th>
              </tr>
            </thead>
            <tbody>
              {q.items.map((it, idx) => {
                const tot = it.quantita * it.prezzo_unitario * (1 - (it.sconto_percent || 0) / 100);
                return (
                  <tr key={idx} className="border-b border-[#E2E8F0]">
                    <td className="py-3">
                      <div className="font-semibold">{it.descrizione}</div>
                      {it.note && <div className="text-xs text-[#64748B] mt-0.5">{it.note}</div>}
                    </td>
                    <td className="py-3 text-right font-mono">{it.quantita}</td>
                    <td className="py-3 text-right font-mono">{formatCurrency(it.prezzo_unitario)}</td>
                    <td className="py-3 text-right font-mono">{it.sconto_percent ? `${it.sconto_percent}%` : "—"}</td>
                    <td className="py-3 text-right font-mono font-bold">{formatCurrency(tot)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-end pb-8">
          <div className="w-full md:w-80 space-y-2">
            <Row label="Subtotale" value={formatCurrency(q.subtotale)} />
            {Number(q.rivalsa_percent) > 0 && (
              <Row label={`+ ${q.rivalsa_label || "Rivalsa"} ${q.rivalsa_percent}%`} value={formatCurrency(q.rivalsa_importo)} />
            )}
            <Row label={`IVA ${q.iva_percent}%`} value={formatCurrency(q.iva_totale)} />
            {Number(q.ritenuta_percent) > 0 && (
              <Row label={`− ${q.ritenuta_label || "Ritenuta"} ${q.ritenuta_percent}%`} value={`− ${formatCurrency(q.ritenuta_importo)}`} />
            )}
            <div className="h-px bg-[#0A0A0A] my-2" />
            <div className="flex items-center justify-between bg-[#0A0A0A] text-white p-4">
              <span className="text-xs uppercase tracking-[0.2em] font-bold">Totale</span>
              <span className="font-mono text-2xl font-black">{formatCurrency(q.totale)}</span>
            </div>
          </div>
        </div>

        {(q.note || q.condizioni || q.termini_pagamento || q.metodo_pagamento) && (
          <div className="border-t border-[#E2E8F0] pt-6 space-y-4">
            {(q.termini_pagamento || q.metodo_pagamento) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {q.termini_pagamento && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Termini di Pagamento</div>
                    <p className="text-sm font-semibold">{q.termini_pagamento}</p>
                  </div>
                )}
                {q.metodo_pagamento && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Metodo di Pagamento</div>
                    <p className="text-sm font-semibold">{q.metodo_pagamento}</p>
                    {q.azienda?.iban && q.metodo_pagamento.toLowerCase().includes("bonifico") && (
                      <p className="text-xs font-mono text-[#64748B] mt-1">IBAN: {q.azienda.iban}</p>
                    )}
                  </div>
                )}
              </div>
            )}
            {q.note && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Note</div>
                <p className="text-sm whitespace-pre-wrap">{q.note}</p>
              </div>
            )}
            {q.condizioni && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Condizioni</div>
                <p className="text-sm whitespace-pre-wrap text-[#64748B]">{q.condizioni}</p>
              </div>
            )}
          </div>
        )}

        {q.stato === "firmato" && (
          <div className="mt-8 border-t-2 border-[#008A3C] pt-6">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle size={24} weight="fill" className="text-[#008A3C]" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#008A3C] font-bold">Firmato Digitalmente</div>
                <div className="text-sm font-semibold">{q.firmatario_nome} · {formatDate(q.signed_at)}</div>
              </div>
            </div>
            {q.firma_data_url && (
              <div className="border border-[#E2E8F0] rounded-sm p-4 inline-block bg-white">
                <img src={q.firma_data_url} alt="Firma" className="max-h-24" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Manual status actions */}
      <div className="mt-6 swiss-card p-4 no-print flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B]">Cambia Stato Manualmente</div>
          <div className="text-xs text-[#64748B] mt-1">Aggiorna lo stato senza firma del cliente</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {["bozza", "inviato", "accettato", "rifiutato"].map((s) => (
            <button
              key={s}
              onClick={() => changeStatus(s)}
              disabled={q.stato === s}
              data-testid={`status-btn-${s}`}
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.15em] font-bold border rounded-sm transition-all ${
                q.stato === s ? "opacity-40 cursor-not-allowed border-[#E2E8F0]" : "border-[#E2E8F0] hover:border-[#0A0A0A]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
      {/* Email modal */}
      {emailModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 no-print" onClick={() => setEmailModal(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={sendEmail} className="bg-white border border-[#E2E8F0] rounded-sm max-w-lg w-full p-6" data-testid="email-modal">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">{emailModal === "reminder" ? "Promemoria" : "Invia"}</div>
            <h3 className="font-display text-2xl font-bold mb-4">
              {emailModal === "reminder" ? "Promemoria al Cliente" : "Email al Cliente"}
            </h3>
            <p className="text-xs text-[#64748B] mb-4">
              {emailModal === "reminder"
                ? `Promemoria ${q.reminder_count || 0 ? `#${(q.reminder_count || 0) + 1}` : ""} per il preventivo non ancora firmato. Verrà inviata una mail con il link diretto alla firma.`
                : "Verrà inviata al cliente una mail con il link sicuro per visionare e firmare il preventivo."}
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Destinatario *</label>
                <input
                  type="email"
                  required
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  data-testid="email-to-input"
                  className="w-full border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] outline-none"
                  placeholder="cliente@azienda.it"
                />
              </div>
              {emailModal === "send" && (
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5">Messaggio personalizzato (opzionale)</label>
                  <textarea
                    rows={5}
                    value={emailMsg}
                    onChange={(e) => setEmailMsg(e.target.value)}
                    placeholder="Lascia vuoto per usare il messaggio predefinito dalle Impostazioni"
                    className="w-full border border-[#E2E8F0] rounded-sm px-3 py-2 text-sm focus:border-[#0033A0] outline-none"
                    data-testid="email-msg-input"
                  />
                  <p className="text-[10px] text-[#64748B] mt-1">Placeholder disponibili: {"{cliente}"}, {"{numero}"}, {"{azienda}"}, {"{link}"}, {"{totale}"}</p>
                </div>
              )}
              {q.email_sent_at && emailModal === "send" && (
                <div className="text-xs text-[#E65C00] bg-[#E65C00]/5 border border-[#E65C00]/20 px-3 py-2 rounded-sm">
                  ⚠ Già inviata il {formatDate(q.email_sent_at)} a {q.email_sent_to}. Confermi reinvio?
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setEmailModal(null)} className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold border border-[#E2E8F0] rounded-sm hover:bg-[#F4F5F7]">Annulla</button>
              <button type="submit" disabled={sending} className="btn-primary px-5 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2" data-testid="email-send-btn">
                <EnvelopeSimple size={14} weight="bold" />
                {sending ? "Invio…" : (emailModal === "reminder" ? "Invia Promemoria" : "Invia Email")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-[#64748B]">{label}</span>
      <span className="font-mono font-bold">{value}</span>
    </div>
  );
}
